// components/UsdConverter.js
import React, { useRef, useState, useEffect } from 'react';

const Converter = () => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(0);

  useEffect(() => {
    const activation = async() => {
      const response = await fetch('https://pythonusd.onrender.com/activate', {
        method: 'GET',
      });
      if(response.ok){
        console.log('Activated successfully');
      }
      else{
        console.error('Failed to activate');
      }
    }
    activation()
  },[])

  const processFiles = async (files) => {
    let extension, fullName;
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
      fullName = file.name.slice(0, file.name.length - 4);
      const ext = file.name.slice(file.name.length - 4, file.name.length);
      extension = ext;
    });
    setLoading(10)
    try {
      if((!['usdc', 'usda', 'usdz'].includes(extension)) || files.length > 1){
        alert('Please upload USDZ files only');
        setLoading(0);
        return;
      }
      const response = await fetch('https://pythonusd.onrender.com', {
        method: 'POST',
        body: formData,
      });

      const eventSource = new EventSource('https://pythonusd.onrender.com/progress');
      eventSource.onmessage = async(event) => {
        event.data <100 ? setLoading(event.data) : setLoading(99);
        if (event.data == 100) {
          eventSource.close();
          const download = await fetch(`https://pythonusd.onrender.com/download?name=${files[0].name.split('.')[0]}`,{
            method: 'GET',
          })
          if(download.ok){
            console.log('Downloaded successfully');
            setLoading(100);
            const blob = await download.blob();
            const url = window.URL.createObjectURL(blob);
            // Create a temporary link element to trigger the download
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `converted_${fullName}${extension == "usdz"? "usdz": extension == "usda"? "usdc":"usda"}`
            //a.download = `${name}_converted.${extension == "usdz"? "usdz": extension == "usda"? "usdc":"usda"}`;  // Specify the filename
            document.body.appendChild(a);
            a.click();  // Simulate a click to start the download
            // Clean up
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);  // Revoke the URL to release resources
          }
        }
      }

      if (response.ok) {
        console.log('Files sent successfully');
      } else {
        console.error('Failed to send files');
      }
    } catch (error) {
      alert('An error occurred while uploading the files.');
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.items) {
      const files = Array.from(e.dataTransfer.items)
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
      await processFiles(files);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files)
    await processFiles(files);
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  if(loading == 0 || loading == 100){
    return (
      <div
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`rounded-lg w-full h-80 flex justify-center items-center font-semibold cursor-pointer text-center font-roboto p-5 ${
          dragActive ? 'bg-blue-500 text-gray-50' : 'bg-blue-500 text-gray-50'}`}>
        <input
          ref={fileInputRef}
          id='fileInput'
          type="file"
          className="hidden"
          onChange={handleFileSelect}
          multiple
        />
        {dragActive ? 'Drop your folder here!' : 'Click or drag your USD file'}
      </div>
    );    
  }
  else{
    return (
      <div className="rounded-lg w-full h-80 flex flex-col justify-center items-center bg-gray-100 text-center font-roboto p-5 relative">
        <input
          ref={fileInputRef}
          id="fileInput"
          type="file"
          className="hidden"
          onChange={handleFileSelect}
          multiple
        />
        <p className="text-lg mb-2 text-blue-500">
          {loading}% Loading...
        </p>
        <div className="w-4/5 h-5 rounded-lg bg-gray-300 shadow-inner relative overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-lg transition-all duration-300 ease"
            style={{ width: `${loading}%` }}
          />
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Please wait while your folder is being processed...
        </p>
      </div>
    );
    
  }
};

const USDZ_FAQ = () => {
  const [openQuestion, setOpenQuestion] = useState(null);

  const toggleQuestion = (index) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  const faqs = [
    {
      question: "What is the purpose of the USDZ Converter?",
      answer: "Our USDZ converter allows you to easily transform binary USDZ files into text and vice versa. This tool ensures efficient conversion for both iOS and Windows/Android platforms, making it versatile for different 3D applications and use cases."
    },
    {
      question: "Why would I want to convert a USDZ file to text format?",
      answer: "Text-based USDA files are human-readable and compatible with a wider range of tools, especially in web environments like WebGL, which can struggle with binary USDC files. While USDA files are easier to work with for troubleshooting and editing, they are much larger in size. For optimal performance, it's best to convert back to USDC once compatibility is no longer an issue."
    },
    {
      question: "Why is my USDA file larger than a binary USDZ?",
      answer: "Text files, like USDA, store data in human-readable form which results in larger file sizes. Binary USDZ (USDC) files are compressed for performance, resulting in smaller file sizes but are not human-readable."
    },
    {
      question: "What formats do you support?",
      answer: "Our converter supports the conversion between USDZ, USDA, and USDC formats. You can also upload individual USDA/USDC files to be included in a zipped USDZ bundle via Reality Converter, though we do not currently support unzipped folder uploads."
    },
    {
      question: "What is the difference between USDZ, USDA, and USDC?",
      answer: "USDZ is a zipped archive that contains USDA or USDC files along with textures and other assets. USDA is a text-based format, while USDC is a binary, more compact version. USDZ packages these components for easier distribution, especially on Apple devices."
    },
    {
      question: "Why is USDZ useful in 3D applications?",
      answer: "USDZ is highly efficient for delivering complex 3D models with textures, animations, and scene data, particularly in AR (Augmented Reality) applications on iOS. Its compressed format ensures smaller sizes without compromising the data quality."
    },
    {
      question: "Can I convert unzipped USD files?",
      answer: "At the moment, we do not support direct conversion of unzipped USD folders. However, you can still convert USDA or USDC files individually and repackage them using a tool like Apple's Reality Converter."
    },
    {
      question: "How do I ensure my USDZ file includes textures?",
      answer: "Ensure that your texture folder is correctly named '0' when uploading your USDZ file for conversion. This guarantees that textures are bundled properly within the USDZ archive."
    },
    {
      question: "What is USD and why should I use it?",
      answer: "USD (Universal Scene Description) is a high-performance software platform designed for creating, collaborating on, and exchanging animated 3D scenes. It's widely used in film, visual effects, and AR/VR applications for its efficiency in managing complex 3D assets."
    }
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="py-20 pt-20 flex flex-col text-center justify-center">
        <h3 className="text-3xl font-bold text-gray-800 mt-10 text-center">
          Frequently Asked Questions
        </h3>
      </div>
      <div className="space-y-4 w-full px-6">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="transition w-full duration-500 border-b border-gray-300 pb-4"
          >
            <button
              onClick={() => toggleQuestion(index)}
              className="transition duration-500 w-full text-left text-lg font-semibold text-gray-600 hover:text-blue-500"
            >
              {index + 1}. {faq.question}
            </button>
            <div
              className={`transition-all duration-500 overflow-hidden`}
              style={{
                maxHeight: openQuestion === index ? "500px" : "0px"
              }}
            >
              <p className="mt-2 text-gray-600">{faq.answer}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const USDZ_Converter = () => {
  return (
    <div className="flex flex-col pt-40 items-center justify-start min-h-screen bg-gray-50 p-4">
      <h1 className="text-4xl font-bold text-gray-800 mb-20">USDZ File Converter</h1>
      <p className="text-lg text-gray-600 mb-4 text-center">
        Easily convert USDA ↔ USDC files with full data retention
      </p>

      <div className="w-full max-w-4xl border-4 border-dashed border-gray-300 bg-white shadow-lg rounded-lg overflow-hidden">
        <Converter />
      </div>

      <p className="text-sm text-center px-10 text-gray-500 mt-4">We currently do not support unzipped USDZ folders, but you can convert USDZ, USDA, and USDC files individually.</p>
      
      <h2 className="text-3xl font-bold text-gray-800 my-8 mt-20">Try it for yourself</h2>

      <div className="flex justify-center items-center gap-10">
        {/* USDC Item */}
        <div className="flex flex-col items-center">
          <a
            rel="ar"
            href="https://firebasestorage.googleapis.com/v0/b/monet-a06c7.appspot.com/o/USDZ_Converter%2FUSDC%20Robot%20Dog.usdz?alt=media&token=d71ca12b-5a40-470c-92c8-1386af9a1bd9"
          >
            <img
              src="https://firebasestorage.googleapis.com/v0/b/monet-a06c7.appspot.com/o/USDZ_Converter%2FUSDZ%20converter%20thumbnail.jpeg?alt=media&token=0fcb3dff-3f59-41a7-b924-593cf267e780"
              alt="USDZ converter that suppors binary to text and vice versa"
              className="w-44 h-44 object-fill rounded-xl"
            />
          </a>
          <h3 className="text-gray-800 text-center text-xl pt-4">USDC File</h3>
        </div>

        {/* USDA Item */}
        <div className="flex flex-col items-center">
          <a
            rel="ar"
            href="https://firebasestorage.googleapis.com/v0/b/monet-a06c7.appspot.com/o/USDZ_Converter%2FUSDA%20Robot%20Dog.usdz?alt=media&token=a54381b5-694c-4a5f-a183-8eaf54622d6c"
          >
            <img
              src="https://firebasestorage.googleapis.com/v0/b/monet-a06c7.appspot.com/o/USDZ_Converter%2FUSDZ%20converter%20thumbnail.jpeg?alt=media&token=0fcb3dff-3f59-41a7-b924-593cf267e780"
              alt="USDZ converter that suppors binary to text and vice versa"
              className="w-44 h-44 object-fill rounded-xl"
            />
          </a>
          <h3 className="text-gray-800 text-center text-xl pt-4">USDA File</h3>
        </div>
      </div>
      
      <div className="pb-80">
        <USDZ_FAQ />
      </div>
    </div>
  );
};

export default USDZ_Converter;
